import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, MessageCircle } from "lucide-react";

const PHONE = "+41 78 311 15 84";
const PHONE_TEL = "+41783111584";
const WHATSAPP_NUMBER = "41783111584";
const WHATSAPP_MSG_BY_LANG = {
  de: "Hallo OmniServe, ich interessiere mich für Ihre Beratung.",
  en: "Hello OmniServe, I'm interested in your consultation.",
  fr: "Bonjour OmniServe, je souhaite plus d'informations.",
  it: "Salve OmniServe, sono interessato alla vostra consulenza.",
  ta: "வணக்கம் OmniServe, உங்கள் ஆலோசனையில் ஆர்வமாக உள்ளேன்.",
};

// Genuine WhatsApp glyph
const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

export default function FloatingContact({ lang = "de" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MSG_BY_LANG[lang] || WHATSAPP_MSG_BY_LANG.de
  )}`;

  return (
    <div className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 flex flex-col items-end gap-3" data-testid="floating-contact">
      <AnimatePresence>
        {open && (
          <>
            {/* WhatsApp */}
            <motion.a
              key="wa"
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer noopener"
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.8 }}
              transition={{ duration: 0.25, delay: 0.05 }}
              data-testid="fab-whatsapp"
              className="group flex items-center gap-3"
            >
              <span className="hidden md:inline-flex bg-white text-[#0A0A0A] text-sm px-4 py-2 rounded-full shadow-lg border border-[#E9E9E7] whitespace-nowrap">
                WhatsApp uns · {PHONE}
              </span>
              <span className="w-14 h-14 bg-[#25D366] hover:bg-[#20BC5A] text-white rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 transition-colors">
                <WhatsAppIcon size={26} />
              </span>
            </motion.a>

            {/* Phone */}
            <motion.a
              key="call"
              href={`tel:${PHONE_TEL}`}
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.8 }}
              transition={{ duration: 0.25 }}
              data-testid="fab-call"
              className="group flex items-center gap-3"
            >
              <span className="hidden md:inline-flex bg-white text-[#0A0A0A] text-sm px-4 py-2 rounded-full shadow-lg border border-[#E9E9E7] whitespace-nowrap">
                Jetzt anrufen · {PHONE}
              </span>
              <span className="w-14 h-14 bg-[#EA6A1A] hover:bg-[#C9560F] text-white rounded-full flex items-center justify-center shadow-lg shadow-[#EA6A1A]/30 transition-colors">
                <Phone size={22} strokeWidth={2} />
              </span>
            </motion.a>
          </>
        )}
      </AnimatePresence>

      {/* Main toggle */}
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="fab-toggle"
        aria-label={open ? "Kontaktoptionen schliessen" : "Kontaktoptionen öffnen"}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
        className="w-16 h-16 rounded-full bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white flex items-center justify-center shadow-xl transition-colors relative"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={24} strokeWidth={2} />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle size={24} strokeWidth={1.8} />
            </motion.span>
          )}
        </AnimatePresence>

        {/* Subtle pulse ring — only when closed & user has scrolled */}
        {!open && scrolled && (
          <span className="absolute inset-0 rounded-full bg-[#EA6A1A]/30 animate-ping" />
        )}
      </motion.button>
    </div>
  );
}
